import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createBillingPortalSession, getSubscriptionStatus } from '../services/api';

type View = 'success' | 'canceled' | 'unknown';

function normalizePlan(p?: string | null): 'free' | 'basic' | 'pro' | 'beta' {
  const v = (p || '').toLowerCase();
  if (v.includes('beta')) return 'beta';
  if (v.includes('pro')) return 'pro';
  if (v.includes('basic')) return 'basic';
  if (v.includes('free') || v.includes('trial')) return 'free';
  return 'basic';
}

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [statusText, setStatusText] = useState<string>('');
  const [subSummary, setSubSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  // Beta state — when true, we leave the portal button visible but DISABLED
  const [isBeta, setIsBeta] = useState<boolean>(false);
  const [portalLoading, setPortalLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const view: View = useMemo(() => {
    const success = params.get('success');
    const canceled = params.get('canceled');
    if (success === 'true') return 'success';
    if (canceled === 'true') return 'canceled';
    return 'unknown';
  }, [params]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (view === 'success') {
          setStatusText('Paiement confirmé ✅');

          // Let the webhook update the DB, then read subscription status
          try {
            await new Promise((r) => setTimeout(r, 1000));
            const s = await getSubscriptionStatus(); // { subscription, hasActiveSubscription }
            const rawPlan = s?.subscription?.plan || 'free';
            const plan = normalizePlan(rawPlan);
            const next =
              (s?.subscription as any)?.currentPeriodEnd
                ? new Date((s.subscription as any).currentPeriodEnd).toLocaleDateString('fr-FR')
                : '—';

            if (!mounted) return;

            setIsBeta(plan === 'beta');
            const planLabel = plan === 'beta' ? 'PRO (Beta Gratuit)' : (plan.toUpperCase());
            setSubSummary(`Plan: ${planLabel} • Prochaine échéance: ${next}`);
          } catch {
            // ignore — UI still shows success
          }
        } else if (view === 'canceled') {
          setStatusText('Paiement annulé');
        } else {
          setStatusText('Statut inconnu');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [view]);

  const openPortal = async () => {
    // Keep the button visible but disabled in Beta
    if (isBeta) {
      setError("Indisponible pendant la Beta gratuite.");
      return;
    }
    setPortalLoading(true);
    setError('');
    try {
      const { url } = await createBillingPortalSession();
      if (!url) throw new Error('Portail indisponible.');
      window.location.href = url;
    } catch (e: any) {
      setError(e.message || 'Erreur portail de facturation.');
    } finally {
      setPortalLoading(false);
    }
  };

  const goDashboard = () => navigate('/dashboard/subscription');

  return (
    <main style={{ maxWidth: 680, margin: '40px auto', padding: '0 16px' }}>
      <div
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 1px 6px rgba(0,0,0,.05)',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{statusText}</h1>

        {loading ? (
          <p style={{ marginTop: 12, color: '#6b7280' }}>Mise à jour…</p>
        ) : (
          <>
            {view === 'success' && (
              <>
                <p style={{ marginTop: 12 }}>
                  Merci ! Votre paiement a été traité. Vous pouvez gérer votre abonnement
                  et récupérer vos factures dans le portail de facturation.
                </p>
                {subSummary && (
                  <p style={{ marginTop: 8, color: '#374151' }}>
                    {subSummary}
                  </p>
                )}
                {isBeta && (
                  <div
                    style={{
                      marginTop: 8,
                      background: '#ecfdf5',
                      color: '#065f46',
                      border: '1px solid #a7f3d0',
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontWeight: 600,
                    }}
                  >
                    Beta gratuite: la facturation est temporairement désactivée. Les boutons restent visibles mais inactifs.
                  </div>
                )}
              </>
            )}

            {view === 'canceled' && (
              <p style={{ marginTop: 12 }}>
                Vous avez annulé le paiement. Aucun montant n’a été débité.
              </p>
            )}

            {view === 'unknown' && (
              <p style={{ marginTop: 12 }}>
                Impossible de déterminer le statut du paiement. Si besoin, ouvrez le
                portail de facturation pour vérifier.
              </p>
            )}

            {error && (
              <div
                style={{
                  marginTop: 12,
                  background: '#ffebee',
                  color: '#b71c1c',
                  border: '1px solid #ffcdd2',
                  borderRadius: 8,
                  padding: '8px 10px',
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button
                onClick={goDashboard}
                style={{
                  border: 0,
                  borderRadius: 10,
                  padding: '.65rem 1rem',
                  fontWeight: 800,
                  minHeight: 44,
                  cursor: 'pointer',
                  background: '#2563eb',
                  color: '#fff',
                }}
              >
                Retour à l’abonnement
              </button>

              <button
                onClick={openPortal}
                disabled={portalLoading || isBeta}
                style={{
                  border: 0,
                  borderRadius: 10,
                  padding: '.65rem 1rem',
                  fontWeight: 800,
                  minHeight: 44,
                  cursor: (portalLoading || isBeta) ? 'not-allowed' : 'pointer',
                  background: (portalLoading || isBeta) ? '#c7c7c7' : '#111827',
                  color: '#fff',
                }}
                title={isBeta ? 'Indisponible pendant la Beta gratuite.' : undefined}
              >
                {portalLoading ? 'Ouverture…' : 'Ouvrir le portail de facturation'}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
